<template>
  <b-nav-item-dropdown
    v-if="aplicavel"
    id="budget-saldo-widget"
    ref="dropdown"
    class="budget-saldo-dropdown"
    right
    menu-class="budget-saldo-menu"
  >
    <!-- Toggle (chip no header) -->
    <template #button-content>
      <div :id="`budget-chip`" class="budget-chip" :class="chipClass">
        <feather-icon icon="DollarSignIcon" size="16" class="budget-chip-icon" />
        <span v-if="saldoPorRepresentante" class="budget-chip-value">
          Budget · {{ quantidadeRepresentantes }} {{ quantidadeRepresentantes === 1 ? 'representante' : 'representantes' }}
        </span>
        <template v-else-if="temCategorias">
          <span class="budget-chip-cat">ELT</span>
          <span class="budget-chip-value">{{ catSaldo('ELT').indisponivel ? 'Pendente' : formatarMoeda(catSaldo('ELT').disponivel) }}</span>
          <span class="budget-chip-sep">·</span>
          <span class="budget-chip-cat">GL</span>
          <span class="budget-chip-value">{{ catSaldo('GL').indisponivel ? 'Pendente' : formatarMoeda(catSaldo('GL').disponivel) }}</span>
        </template>
        <span v-else class="budget-chip-value">{{ chipLabel }}</span>
      </div>
      <b-tooltip target="budget-chip" triggers="hover" placement="bottom">
        {{ tooltip }}
      </b-tooltip>
    </template>

    <!-- Cabeçalho -->
    <li class="budget-menu-header">
      <span class="budget-menu-title">Budget por representante</span>
    </li>

    <li v-if="saldoPorRepresentante">
      <budget-por-representante :saldo="saldoPorRepresentante" />
    </li>
    <template v-else>
    <!-- ───── SUPERVISOR ───── -->
    <template v-if="isSupervisor">
      <li v-if="saldo.semBudget" class="budget-empty">
        Nenhum budget vigente no momento.
        <div v-for="cat in ['ELT', 'GL']" :key="`aviso-${cat}`">
          <span v-if="catAviso(cat)" class="budget-aviso">{{ cat }}: {{ catAviso(cat) }}</span>
        </div>
      </li>
      <template v-else>
        <li class="budget-totais">
          <div class="budget-total-line">
            <span>Total</span><span>{{ formatarMoeda(saldo.budget.valor) }}</span>
          </div>
          <div class="budget-total-line">
            <span>Gasto</span><span class="text-danger">{{ formatarMoeda(saldo.budget.valorgasto) }}</span>
          </div>
          <div v-if="rascunhoTotal > 0" class="budget-total-line">
            <span>Em rascunho</span><span class="text-info">{{ formatarMoeda(rascunhoTotal) }}</span>
          </div>
          <div class="budget-total-line budget-total-disp">
            <span>Disponível</span>
            <span :class="saldo.budget.disponivel > 0 ? 'text-success' : 'text-danger'">
              {{ formatarMoeda(saldo.budget.disponivel) }}
            </span>
          </div>
        </li>

        <li class="budget-sub">Representantes</li>
        <li v-if="!saldo.representantes.length" class="budget-empty">
          Nenhuma alocação individual definida.
        </li>
        <li
          v-for="rep in saldo.representantes"
          :key="rep.representanteid"
          class="budget-rep-line"
        >
          <span class="budget-rep-nome" :title="rep.representante_nome || rep.representanteid">
            {{ rep.representante_nome || rep.representanteid }}
          </span>
          <span :class="rep.disponivel > 0 ? 'text-success' : 'text-danger'">
            {{ formatarMoeda(rep.disponivel) }}
          </span>
        </li>

        <li class="budget-footer">
          <b-link @click="irParaDashboard">Ver dashboard de budget</b-link>
        </li>
      </template>
    </template>

    <!-- ───── REPRESENTANTE ───── -->
    <template v-else>
      <li v-if="saldo.saldoParcial" class="budget-empty text-warning">
        Consulta parcial. Categoria pendente não tem saldo disponível confirmado e não libera doações.
      </li>
      <li v-if="saldo.semAlocacao" class="budget-empty">
        Você ainda não tem budget individual alocado.
      </li>
      <template v-else-if="temCategorias">
        <template v-for="cat in ['ELT', 'GL']">
          <li :key="`sub-${cat}`" class="budget-sub">
            {{ cat }} — {{ cat === 'ELT' ? 'English Language Teaching' : 'Global Languages' }}
          </li>
          <li :key="`tot-${cat}`" class="budget-totais">
            <div v-if="catSaldo(cat).indisponivel" class="budget-aviso">
              Responsabilidade pendente — saldo indisponível. Solicite a revisão ao administrador.
            </div>
            <template v-else-if="catSaldo(cat).temAlocacao">
              <div class="budget-total-line">
                <span>Alocado</span><span>{{ formatarMoeda(catSaldo(cat).valor) }}</span>
              </div>
              <div class="budget-total-line">
                <span>Gasto</span><span class="text-danger">{{ formatarMoeda(catSaldo(cat).valorgasto) }}</span>
              </div>
              <div v-if="Number(catSaldo(cat).rascunho) > 0" class="budget-total-line">
                <span>Em rascunho</span><span class="text-info">{{ formatarMoeda(catSaldo(cat).rascunho) }}</span>
              </div>
              <div class="budget-total-line budget-total-disp">
                <span>Disponível</span>
                <span :class="catSaldo(cat).disponivel > 0 ? 'text-success' : 'text-danger'">
                  {{ formatarMoeda(catSaldo(cat).disponivel) }}
                </span>
              </div>
              <div v-if="Number(catSaldo(cat).rascunho) > 0" class="budget-total-line text-muted">
                <span>Após rascunhos</span>
                <span :class="catSaldo(cat).disponivelAposRascunhos > 0 ? 'text-success' : 'text-danger'">
                  {{ formatarMoeda(catSaldo(cat).disponivelAposRascunhos) }}
                </span>
              </div>
            </template>
            <div v-else class="budget-total-line" :class="catAviso(cat) ? 'budget-aviso' : 'text-muted'">
              <span v-if="catSaldo(cat).temBudget">Sem alocação</span>
              <span v-else>{{ catAviso(cat) || 'Sem budget vigente' }}</span>
            </div>
          </li>
        </template>
      </template>
      <li v-else class="budget-totais">
        <div class="budget-total-line">
          <span>Alocado</span><span>{{ formatarMoeda(saldo.valor) }}</span>
        </div>
        <div class="budget-total-line">
          <span>Gasto</span><span class="text-danger">{{ formatarMoeda(saldo.valorgasto) }}</span>
        </div>
        <div class="budget-total-line budget-total-disp">
          <span>Disponível</span>
          <span :class="saldo.disponivel > 0 ? 'text-success' : 'text-danger'">
            {{ formatarMoeda(saldo.disponivel) }}
          </span>
        </div>
      </li>
    </template>
    </template>
  </b-nav-item-dropdown>
</template>

<script>
import { BNavItemDropdown, BLink, BTooltip } from 'bootstrap-vue';
import { mapGetters } from 'vuex';
import BudgetPorRepresentante from './BudgetPorRepresentante.vue';

export default {
  name: 'BudgetSaldoWidget',
  components: {
    BNavItemDropdown,
    BLink,
    BTooltip,
    BudgetPorRepresentante,
  },
  computed: {
    ...mapGetters({ saldo: 'Budget/meuSaldo' }),
    saldoPorRepresentante() {
      if (!this.saldo) return null;
      return this.saldo.visaoRepresentantes || (Array.isArray(this.saldo.itens) ? this.saldo : null);
    },
    quantidadeRepresentantes() {
      const vinculados = this.saldoPorRepresentante?.representantes;
      const origem = Array.isArray(vinculados) && vinculados.length
        ? vinculados
        : (this.saldoPorRepresentante?.itens || []);
      return new Set(origem.map(item => String(item.representanteid))).size;
    },
    aplicavel() {
      return !!(this.saldo && this.saldo.aplicavel);
    },
    isSupervisor() {
      return this.saldo && this.saldo.tipo === 'supervisor';
    },
    // Chip com resumo ELT/GL: apenas para REPRESENTANTE com saldos por categoria.
    temCategorias() {
      return !this.isSupervisor
        && !!(this.saldo && this.saldo.categorias)
        && !this.saldo.semAlocacao;
    },
    chipLabel() {
      if (!this.aplicavel) return '';
      if (this.isSupervisor) {
        if (this.saldo.semBudget || !this.saldo.budget) return 'Budget';
        return this.formatarMoeda(this.saldo.budget.disponivel);
      }
      if (this.saldo.semAlocacao) return 'Budget';
      return this.formatarMoeda(this.saldo.disponivel);
    },
    chipClass() {
      if (this.saldoPorRepresentante) return 'budget-chip--neutral';
      if (this.saldo.saldoParcial) return 'budget-chip--neutral';
      const disp = this.isSupervisor
        ? (this.saldo.budget ? this.saldo.budget.disponivel : null)
        : (this.saldo.semAlocacao ? null : this.saldo.disponivel);
      if (disp === null || disp === undefined) return 'budget-chip--neutral';
      return disp > 0 ? 'budget-chip--ok' : 'budget-chip--zero';
    },
    rascunhoTotal() {
      return Number(this.saldo && this.saldo.rascunhos && this.saldo.rascunhos.total) || 0;
    },
    tooltip() {
      if (this.saldoPorRepresentante) return 'Consultar saldo ELT e GL de cada representante vinculado ao seu usuário';
      if (this.saldo.saldoParcial) return 'Consulta parcial: responsabilidade pendente em uma ou mais categorias';
      return this.isSupervisor
        ? 'Budget disponível da sua carteira'
        : 'Seu budget disponível';
    },
  },
  methods: {
    // Aviso quando não há budget vigente na categoria: vencido ou ainda não iniciado.
    catAviso(cat) {
      const info = this.catSaldo(cat).budgetNaoVigente;
      if (!info) return '';
      if (info.situacao === 'vencido') return `Budget vencido em ${this.formatarData(info.datatermino)}`;
      if (info.situacao === 'futuro') return `Budget inicia em ${this.formatarData(info.datainicio)}`;
      return '';
    },
    formatarData(d) {
      if (!d) return '';
      const s = String(d).slice(0, 10);
      const [a, m, dia] = s.split('-');
      return (a && m && dia) ? `${dia}/${m}/${a}` : s;
    },
    catSaldo(cat) {
      const cs = this.saldo && this.saldo.categorias;
      return (cs && cs[cat]) || {
        temBudget: false, temAlocacao: false, valor: 0, valorgasto: 0, disponivel: 0, rascunho: 0, disponivelAposRascunhos: 0,
      };
    },
    formatarMoeda(v) {
      const n = Number(v) || 0;
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    irParaDashboard() {
      if (this.$refs.dropdown) this.$refs.dropdown.hide(true);
      if (this.$route.name !== 'budget-dashboard') {
        this.$router.push({ name: 'budget-dashboard' }).catch(() => {});
      }
    },
    carregar() {
      this.$store.dispatch('Budget/carregarMeuSaldo').catch(() => {});
    },
  },
  mounted() {
    this.carregar();
    // Atualiza quando uma doação consome budget (mesmo evento do fluxo de pedido).
    this.$root.$on('budget:updated', this.carregar);
  },
  beforeDestroy() {
    this.$root.$off('budget:updated', this.carregar);
  },
};
</script>

<style lang="scss" scoped>
.budget-saldo-dropdown {
  list-style: none;

  ::v-deep > a.nav-link {
    display: flex;
    align-items: center;
    padding: 0 0.35rem;
  }

  ::v-deep .dropdown-toggle::after {
    display: none;
  }
}

.budget-chip {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  height: 30px;
  padding: 0 0.6rem;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.78rem;
  line-height: 1;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}

.budget-chip-icon { flex-shrink: 0; }

// Mobile: só o ícone $ (colorido pelo saldo); valores ficam no dropdown.
@media (max-width: 767.98px) {
  .budget-chip {
    gap: 0;
    padding: 0 0.45rem;
  }
  .budget-chip-cat,
  .budget-chip-value,
  .budget-chip-sep {
    display: none;
  }
}

.budget-chip-cat {
  font-size: 0.66rem;
  font-weight: 700;
  opacity: 0.75;
  letter-spacing: 0.03em;
}
.budget-chip-sep { opacity: 0.5; }

.budget-chip--ok {
  background: rgba(40, 199, 111, 0.12);
  color: #28c76f;
}
.budget-chip--zero {
  background: rgba(234, 84, 85, 0.12);
  color: #ea5455;
}
.budget-chip--neutral {
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
}

::v-deep .budget-saldo-menu {
  min-width: 270px;
  padding: 0;
  overflow: hidden;
}

.budget-menu-header {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.budget-menu-title {
  font-weight: 700;
  font-size: 0.85rem;
}

.budget-totais {
  padding: 0.6rem 1rem;
}
.budget-total-line {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 0.15rem 0;
}
.budget-total-disp {
  margin-top: 0.25rem;
  padding-top: 0.45rem;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
  font-weight: 700;
}

.budget-sub {
  padding: 0.4rem 1rem 0.15rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted, #94a3b8);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.budget-rep-line {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8rem;
  padding: 0.22rem 1rem;
}
.budget-rep-nome {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.budget-aviso {
  color: #ff9f43;
  font-weight: 600;
}

.budget-empty {
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  color: var(--text-muted, #94a3b8);
}

.budget-footer {
  padding: 0.55rem 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  text-align: center;

  a {
    font-size: 0.8rem;
    font-weight: 600;
  }
}

body.dark-layout {
  ::v-deep .budget-saldo-menu {
    background: #283046;
  }
  .budget-menu-header,
  .budget-sub,
  .budget-footer {
    border-color: rgba(255, 255, 255, 0.08);
  }
  .budget-total-disp {
    border-color: rgba(255, 255, 255, 0.12);
  }
}
</style>
